import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Users, Target, Building2, Settings, Shield, Heart, GraduationCap, Rocket } from 'lucide-react';
import { motion } from 'framer-motion';

export const ProgramsPage = () => {
  // All 8 Flagship Programs
  const flagshipPrograms = [
    {
      id: 'accelerator',
      title: 'Legacy District Impact Accelerator™',
      subtitle: 'Comprehensive District Transformation',
      desc: 'Our flagship program that integrates all seven pillars of the Legacy Leadership System into a cohesive district transformation experience. Designed for superintendents and executive cabinets committed to systemic change.',
      results: ['Full district assessment', 'Strategic roadmap development', '12-month implementation support', 'Quarterly executive coaching'],
      ideal: 'Districts seeking comprehensive, multi-year transformation partnerships',
      icon: Rocket,
      featured: true,
      link: '/accelerator',
    },
    {
      id: 'cabinet',
      title: 'Legacy Executive Cabinet Academy™',
      subtitle: 'Executive Leadership Development',
      desc: 'Transform your district\'s senior leadership team into a high-performing executive cabinet aligned around shared vision, strategic priorities, and accountability systems.',
      results: ['Cabinet effectiveness assessment', 'Strategic alignment sessions', 'Communication protocols', 'Executive coaching'],
      ideal: 'Superintendents developing new or existing cabinet teams',
      icon: Users,
    },
    {
      id: 'governance',
      title: 'Governance in Alignment™',
      subtitle: 'Board-Superintendent Excellence',
      desc: 'Strengthen board-superintendent relationships, clarify governance roles, and create alignment that supports sustainable student success.',
      results: ['Board self-assessment', 'Role clarity workshops', 'Strategic planning facilitation', 'Policy alignment review'],
      ideal: 'Boards and superintendents seeking improved governance',
      icon: Shield,
    },
    {
      id: 'ready',
      title: '100% Ready Initiative™',
      subtitle: 'College & Career Readiness Systems',
      desc: 'Achieve 100% College & Career Readiness through systematic implementation, progress monitoring, and accountability structures that ensure every student graduates prepared.',
      results: ['CCRI assessment and gap analysis', 'Implementation roadmap', 'Dashboard development', 'Quarterly reviews'],
      ideal: 'Districts committed to 100% CCR for all students',
      icon: GraduationCap,
    },
    {
      id: 'cte',
      title: 'CTE Director Launchpad™',
      subtitle: 'Career & Technical Education Excellence',
      desc: 'Comprehensive development program for new and aspiring CTE directors, covering Perkins compliance, industry partnerships, career academies, and workforce development.',
      results: ['CTE program assessment', 'Industry partnership strategy', 'Perkins compliance review', 'Career pathway development'],
      ideal: 'New CTE directors and districts building CTE programs',
      icon: Settings,
    },
    {
      id: 'deescalation',
      title: 'Effective De-Escalation & Restraint™',
      subtitle: 'Safety & Crisis Leadership',
      desc: 'Train school leaders and staff in effective de-escalation strategies, appropriate restraint procedures, and crisis leadership that prioritizes student and staff safety.',
      results: ['Safety assessment', 'Staff training certification', 'Policy development', 'Crisis response protocols'],
      ideal: 'Districts prioritizing student and staff safety',
      icon: Heart,
    },
    {
      id: 'middle',
      title: 'Middle Grades Legacy Initiative™',
      subtitle: 'Middle School Transformation',
      desc: 'Specialized program for transforming middle school performance through targeted intervention strategies, ACT preparation, and engagement systems.',
      results: ['Middle grades assessment', 'Intervention systems design', 'Teacher development', 'Student engagement strategies'],
      ideal: 'Districts focused on middle school improvement',
      icon: Building2,
    },
    {
      id: 'legacy',
      title: 'Tell the Legacy™',
      subtitle: 'Communication & Community',
      desc: 'Build your district\'s communication strategy, community partnerships, and legacy narrative that celebrates success and builds lasting stakeholder support.',
      results: ['Communication audit', 'Partnership strategy', 'Legacy narrative development', 'Stakeholder engagement plan'],
      ideal: 'Districts building community support and celebrating success',
      icon: Target,
    },
  ];

  return (
    <div className="pt-20 lg:pt-24" data-testid="programs-page">
      {/* Hero Section */}
      <section className="section-padding bg-[#0B1F3A]" data-testid="programs-hero">
        <div className="container-premium">
          <div className="max-w-3xl">
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="overline text-[#4F8A2F] mb-4"
            >
              Signature Programs
            </motion.p>
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6"
            >
              Eight Flagship Programs for District Transformation
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-slate-300 text-lg lg:text-xl leading-relaxed"
            >
              Each program is designed to address specific challenges while integrating seamlessly into the Legacy Leadership System. Transform your district one pillar at a time—or accelerate change with our comprehensive Accelerator.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Programs Grid */}
      <section className="section-padding bg-white" data-testid="programs-grid">
        <div className="container-premium">
          <div className="space-y-8">
            {flagshipPrograms.map((program, index) => (
              <motion.div
                key={program.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className={`rounded-2xl overflow-hidden ${
                  program.featured 
                    ? 'bg-gradient-to-br from-[#0B1F3A] to-[#172033] text-white p-8 lg:p-12' 
                    : 'bg-[#F7F8F6] p-8 lg:p-10'
                }`}
                data-testid={`program-${program.id}`}
              >
                <div className="grid lg:grid-cols-3 gap-8 items-start">
                  {/* Program Info */}
                  <div className="lg:col-span-2">
                    <div className="flex items-center gap-4 mb-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        program.featured ? 'bg-[#4F8A2F]' : 'bg-[#0B1F3A]/10'
                      }`}>
                        <program.icon className={`w-6 h-6 ${program.featured ? 'text-[#0B1F3A]' : 'text-[#0B1F3A]'}`} strokeWidth={1.5} />
                      </div>
                      {program.featured && (
                        <span className="text-xs font-semibold tracking-wider uppercase text-[#4F8A2F] bg-[#4F8A2F]/20 px-3 py-1 rounded-full">
                          Flagship Program
                        </span>
                      )}
                    </div>
                    
                    <p className={`text-xs font-semibold tracking-wider uppercase mb-2 ${
                      program.featured ? 'text-[#4F8A2F]' : 'text-[#4F8A2F]'
                    }`}>
                      {program.subtitle}
                    </p>
                    
                    <h2 className={`font-display text-2xl lg:text-3xl font-bold mb-4 ${
                      program.featured ? 'text-white' : 'text-[#172033]'
                    }`}>
                      {program.title}
                    </h2>
                    
                    <p className={`text-base lg:text-lg leading-relaxed mb-6 ${
                      program.featured ? 'text-slate-300' : 'text-slate-600'
                    }`}>
                      {program.desc}
                    </p>

                    <div className="mb-6">
                      <p className={`text-sm font-semibold mb-3 ${program.featured ? 'text-white' : 'text-[#172033]'}`}>
                        Key Deliverables:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {program.results.map((result, i) => (
                          <span 
                            key={i} 
                            className={`text-xs px-3 py-1.5 rounded-full ${
                              program.featured 
                                ? 'bg-white/10 text-white' 
                                : 'bg-[#0B1F3A]/10 text-[#0B1F3A]'
                            }`}
                          >
                            {result}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Ideal Client & CTA */}
                  <div className={`lg:border-l lg:pl-8 ${program.featured ? 'border-white/20' : 'border-slate-200'}`}>
                    <p className={`text-sm font-semibold mb-2 ${program.featured ? 'text-[#4F8A2F]' : 'text-[#4F8A2F]'}`}>
                      Ideal For:
                    </p>
                    <p className={`text-sm leading-relaxed mb-6 ${program.featured ? 'text-slate-300' : 'text-slate-600'}`}>
                      {program.ideal}
                    </p>
                    <Link to={program.link || '/contact'}>
                      <Button 
                        className={`w-full rounded-full font-body ${
                          program.featured 
                            ? 'bg-[#4F8A2F] hover:bg-[#6FAE3A] text-[#0B1F3A]' 
                            : 'bg-[#0B1F3A] hover:bg-[#172033] text-white'
                        }`}
                      >
                        {program.featured ? 'Explore the Accelerator' : 'Learn More'}
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#F7F8F6]">
        <div className="container-premium text-center">
          <p className="overline mb-4">Not Sure Where to Start?</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#172033] mb-6">
            Let's Find the Right Fit
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto mb-10">
            Schedule a complimentary strategy session to discuss your district's unique challenges and identify which programs align with your goals.
          </p>
          <Link to="/contact">
            <Button className="bg-[#0B1F3A] hover:bg-[#172033] text-white rounded-full px-10 py-5 font-body text-lg">
              Schedule a Strategy Session
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
